import { createFileRoute } from '@tanstack/react-router';
import type { PaginationState } from '@tanstack/react-table';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback, useMemo, useState } from 'react';
import { match } from 'ts-pattern';
import ActionButton from '@/components/Button/ActionButton';
import { FillScreen, HStack, ScrollableRow } from '@/components/Layout';
import { HeaderTabs, type TabWithIndicatorProps } from '@/components/Navigation/HeaderTabs';
import { Text } from '@/components/Typography/Text';
import { ImportJobListTable, MemberTypeSelectDialog, useImportJobList } from '@/features/people-csv-import';
import type { ImportType } from '@/lib/api/orpc/contracts/people-csv-async-import';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { DownloadTemplateMenu } from './-components/DownloadTemplateMenu';
import { PeopleCsvUploadDialog } from './-components/PeopleCsvUploadDialog';
import { CsvImportListSearchSchema } from './-zod-schema';

/** 一覧タブの種別。具体的なメンバー種別 + 全種別横断の `ALL` */
type ListTabValue = ImportType | 'ALL';
const MEMBER_TYPE_TABS: ListTabValue[] = ['EMPLOYEE', 'BOARD_MEMBER', 'CONTRACTOR', 'COLLABORATOR', 'ALL'];
const CsvImportList = () => {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  /** All タブからの導線でのみ使うメンバー種別選択ダイアログ */
  const [isMemberTypeDialogOpen, setIsMemberTypeDialogOpen] = useState(false);
  /** アップロードダイアログの開閉状態と対象種別（具体タブは即時確定、All は選択ダイアログ経由で確定） */
  const [uploadDialogType, setUploadDialogType] = useState<ImportType | undefined>(undefined);
  /**
   * ダイアログを開くたびに変更し、`key` として渡すことで内部状態（選択済みファイル等）を破棄する。
   * useEffect でのリセットは cascading render を招くため、再マウントで対応する。
   */
  const [uploadDialogInstanceKey, setUploadDialogInstanceKey] = useState(0);
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.person.csvImport'
  });

  // 動的キー（テンプレートリテラル）を避け、importType ごとに明示キーで解決する
  const memberTypeLabel = (type: ListTabValue): string => match(type).with('EMPLOYEE', () => t('memberType.employee')).with('BOARD_MEMBER', () => t('memberType.boardMember')).with('CONTRACTOR', () => t('memberType.contractor')).with('COLLABORATOR', () => t('memberType.collaborator')).with('ALL', () => t('memberType.all')).exhaustive();
  const {
    data,
    isLoading
  } = useImportJobList({
    page: search.page,
    pageSize: search.pageSize,
    orderDirection: 'desc',
    importType: search.type === 'ALL' ? undefined : search.type
  });
  const pagination: PaginationState = {
    pageIndex: search.page - 1,
    pageSize: search.pageSize
  };
  const handlePaginationChange = useCallback((updater: PaginationState | ((old: PaginationState) => PaginationState)) => {
    const current: PaginationState = {
      pageIndex: search.page - 1,
      pageSize: search.pageSize
    };
    const next = typeof updater === 'function' ? updater(current) : updater;
    navigate({
      search: {
        ...search,
        page: next.pageIndex + 1,
        pageSize: next.pageSize
      }
    });
  }, [navigate, search]);
  const handleMemberTypeTabClick = useCallback((type: ListTabValue) => {
    navigate({
      search: {
        ...search,
        type,
        page: 1
      }
    });
  }, [navigate, search]);
  const handleSelectJob = useCallback((importId: string) => {
    navigate({
      to: '/person/csv-import/$importId',
      params: {
        importId
      }
    });
  }, [navigate]);

  /** タブ選択中の種別で「一括追加」ボタン押下時。All タブのみ種別選択ダイアログを挟む */
  const handleBulkImportClick = useCallback(() => {
    if (search.type === 'ALL') {
      setIsMemberTypeDialogOpen(true);
      return;
    }
    setUploadDialogType(search.type);
    setUploadDialogInstanceKey(key => key + 1);
  }, [search.type]);

  /** メンバー種別選択ダイアログ（All タブ経由）で種別を確定 → アップロードダイアログへ */
  const handleConfirmType = useCallback((type: ImportType) => {
    setIsMemberTypeDialogOpen(false);
    setUploadDialogType(type);
    setUploadDialogInstanceKey(key => key + 1);
  }, []);

  /** アップロードダイアログでの検証開始成功 → 詳細画面へ遷移 */
  const handleUploaded = useCallback((importId: string) => {
    setUploadDialogType(undefined);
    navigate({
      to: '/person/csv-import/$importId',
      params: {
        importId
      }
    });
  }, [navigate]);

  /**
   * ページ上部の Import / Export 切り替えタブ（ルート遷移）。
   * 現状は Import（この画面自身 `/person/csv-import`）のみ。Export 機能が増えたら
   * `/person/csv-export` へのタブを配列へ追加する。押下時も現在の絞り込み・ページを維持する。
   */
  const menuTabs = useMemo<TabWithIndicatorProps[]>(() => [{
    to: '/person/csv-import',
    search: {
      type: search.type,
      page: search.page,
      pageSize: search.pageSize
    },
    activeOptions: {
      exact: true
    },
    children: t('list.menuTabs.import')
  }], [search.type, search.page, search.pageSize, t]);
  return <FillScreen className="flex flex-col">
      <div className="shrink-0 border-b border-neutral-muted-border bg-neutral-fill px-12 pb-0 pt-4">
        <HStack className="items-center justify-between gap-6">
          <div className="flex min-w-0 flex-1 flex-col pt-2 pb-3">
            <Text variant="h2" className="capitalize">
              {t('list.title')}
            </Text>
          </div>
          <DownloadTemplateMenu />
        </HStack>

        <HeaderTabs tabs={menuTabs} />
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-5 bg-neutral-bottom-fill px-12 py-6">
        <HStack className="w-full items-center justify-between">
          <ScrollableRow contentClassName="items-center gap-2">
            {MEMBER_TYPE_TABS.map(type => <ActionButton key={type} variant={search.type === type ? 'neutral' : 'ghost'} className={cn('relative rounded border-none', search.type === type ? 'text-neutral-on-fill' : 'opacity-60')} onClick={() => handleMemberTypeTabClick(type)}>
                {memberTypeLabel(type)}
              </ActionButton>)}
          </ScrollableRow>
          <ActionButton variant="secondary" icon="ImportIcon" onClick={handleBulkImportClick}>
            {search.type === 'ALL' ? t('list.bulkImportButtonAll') : t('list.bulkImportButton', {
            memberType: memberTypeLabel(search.type)
          })}
          </ActionButton>
        </HStack>

        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <ImportJobListTable items={data?.items ?? []} isLoading={isLoading} totalCount={data?.count ?? 0} pageCount={data?.maxPage ?? 1} pagination={pagination} onPaginationChange={handlePaginationChange} onSelect={handleSelectJob} />
        </div>
      </div>

      <MemberTypeSelectDialog open={isMemberTypeDialogOpen} onOpenChange={setIsMemberTypeDialogOpen} onConfirm={handleConfirmType} />

      <PeopleCsvUploadDialog key={uploadDialogInstanceKey} open={uploadDialogType != null} onOpenChange={open => {
      if (!open) setUploadDialogType(undefined);
    }} importType={uploadDialogType ?? 'EMPLOYEE'} onUploaded={handleUploaded} />
    </FillScreen>;
};
export const Route = createFileRoute('/_staff/person/csv-import/')({
  validateSearch: zodValidator(CsvImportListSearchSchema),
  component: CsvImportList
});